import React, { useCallback, useEffect, useRef, useState } from "react";
import { db } from "../firebase";
import { collection, doc, getDoc, getDocs, updateDoc, serverTimestamp } from "firebase/firestore";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Save as SaveIcon,
  LinkOff as LinkOffIcon,
  Link as LinkIcon,
  AutoFixHigh as AutoLinkIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

const NODE_TYPES = ["entrance", "aisle", "junction", "checkout", "shelf"];

const NODE_COLORS = {
  entrance: "#2E7D32",
  aisle: "#E65100",
  junction: "#757575",
  checkout: "#6A1B9A",
  shelf: "#00838F",
};

const PRODUCTS_COLLECTION = collection(db, "products");

// 1.5% of map dimension triggers axis snap during drag
const SNAP_THRESHOLD = 0.015;

// Arrow-key nudge: normal = ~2 px on a 1000 px map, Shift = 5×
const ARROW_STEP = 0.002;
const ARROW_STEP_LARGE = 0.010;

function generateId() {
  return "node_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function dist(a, b) {
  return Math.sqrt((a.x_ratio - b.x_ratio) ** 2 + (a.y_ratio - b.y_ratio) ** 2);
}

export default function RouteEditor({ mapUrl, mapId }) {
  const imgRef = useRef(null);
  const svgRef = useRef(null);
  const containerRef = useRef(null);

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  // Second node in pair — pressing C connects/disconnects the pair
  const [secondNodeId, setSecondNodeId] = useState(null);
  // Snap guide lines shown while dragging { snapX: ratio|null, snapY: ratio|null }
  const [snapIndicator, setSnapIndicator] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [imgRect, setImgRect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [linking, setLinking] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "success" });

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) || null;

  // Load graph from store_maps/{mapId}
  useEffect(() => {
    if (!mapId) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, "store_maps", mapId));
        if (snap.exists()) {
          const data = snap.data();
          setNodes(data.nodes || []);
          setEdges(data.edges || []);
        }
      } catch (e) {
        console.error("Failed to load graph:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [mapId]);

  const updateImgRect = useCallback(() => {
    if (imgRef.current) setImgRect(imgRef.current.getBoundingClientRect());
  }, []);

  useEffect(() => {
    window.addEventListener("resize", updateImgRect);
    return () => window.removeEventListener("resize", updateImgRect);
  }, [updateImgRect]);

  const ratioToSvg = useCallback((xRatio, yRatio) => {
    if (!imgRef.current || !containerRef.current) return { x: 0, y: 0 };
    const iRect = imgRef.current.getBoundingClientRect();
    const cRect = containerRef.current.getBoundingClientRect();
    return {
      x: iRect.left - cRect.left + xRatio * iRect.width,
      y: iRect.top - cRect.top + yRatio * iRect.height,
    };
  }, []);

  const svgToRatio = useCallback((svgX, svgY) => {
    if (!imgRef.current || !containerRef.current) return { x_ratio: 0, y_ratio: 0 };
    const iRect = imgRef.current.getBoundingClientRect();
    const cRect = containerRef.current.getBoundingClientRect();
    const relX = svgX - (iRect.left - cRect.left);
    const relY = svgY - (iRect.top - cRect.top);
    return {
      x_ratio: Math.max(0, Math.min(1, relX / iRect.width)),
      y_ratio: Math.max(0, Math.min(1, relY / iRect.height)),
    };
  }, []);

  // Delete the primary selected node and all its edges
  const handleDeleteNode = useCallback(() => {
    if (!selectedNodeId) return;
    setNodes((prev) => prev.filter((n) => n.id !== selectedNodeId));
    setEdges((prev) =>
      prev.filter((e) => e.from_id !== selectedNodeId && e.to_id !== selectedNodeId)
    );
    setSecondNodeId((prev) => (prev === selectedNodeId ? null : prev));
    setSelectedNodeId(null);
  }, [selectedNodeId]);

  // Toggle edge between two node IDs
  const toggleEdge = useCallback((aId, bId) => {
    const edgeKey = (x, y) => (x < y ? `${x}__${y}` : `${y}__${x}`);
    const eKey = edgeKey(aId, bId);
    setEdges((prev) => {
      const idx = prev.findIndex((ed) => edgeKey(ed.from_id, ed.to_id) === eKey);
      return idx >= 0
        ? prev.filter((_, i) => i !== idx)
        : [...prev, { from_id: aId, to_id: bId }];
    });
  }, []);

  // Nudge selected node by a ratio delta (arrow keys)
  const handleArrowMove = useCallback(
    (dx, dy) => {
      if (!selectedNodeId) return;
      setNodes((prev) =>
        prev.map((n) =>
          n.id === selectedNodeId
            ? {
                ...n,
                x_ratio: Math.max(0, Math.min(1, n.x_ratio + dx)),
                y_ratio: Math.max(0, Math.min(1, n.y_ratio + dy)),
              }
            : n
        )
      );
    },
    [selectedNodeId]
  );

  // Keyboard: Delete, C, and Arrow keys
  useEffect(() => {
    const onKeyDown = (e) => {
      // Never fire when the user is typing in a form field
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if (e.key === "Delete" && selectedNodeId) {
        e.preventDefault();
        handleDeleteNode();
        return;
      }

      if ((e.key === "c" || e.key === "C") && selectedNodeId && secondNodeId) {
        e.preventDefault();
        toggleEdge(selectedNodeId, secondNodeId);
        return;
      }

      if (selectedNodeId) {
        const step = e.shiftKey ? ARROW_STEP_LARGE : ARROW_STEP;
        if (e.key === "ArrowLeft")  { e.preventDefault(); handleArrowMove(-step, 0); }
        if (e.key === "ArrowRight") { e.preventDefault(); handleArrowMove( step, 0); }
        if (e.key === "ArrowUp")    { e.preventDefault(); handleArrowMove(0, -step); }
        if (e.key === "ArrowDown")  { e.preventDefault(); handleArrowMove(0,  step); }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedNodeId, secondNodeId, handleDeleteNode, toggleEdge, handleArrowMove]);

  // Click on SVG background → add new node
  const handleSvgClick = useCallback(
    (e) => {
      if (dragState) return;
      if (e.target !== svgRef.current) return;

      const cRect = containerRef.current.getBoundingClientRect();
      const { x_ratio, y_ratio } = svgToRatio(
        e.clientX - cRect.left,
        e.clientY - cRect.top
      );

      const newNode = { id: generateId(), label: "", type: "junction", x_ratio, y_ratio };
      setNodes((prev) => [...prev, newNode]);
      setSelectedNodeId(newNode.id);
      setSecondNodeId(null);
    },
    [dragState, svgToRatio]
  );

  // Click a node — builds a pair selection; does NOT auto-connect
  const handleNodeClick = useCallback(
    (e, nodeId) => {
      e.stopPropagation();
      if (dragState) return;

      if (selectedNodeId === null) {
        // Nothing selected → this becomes the primary
        setSelectedNodeId(nodeId);
        setSecondNodeId(null);
      } else if (selectedNodeId === nodeId) {
        // Re-clicking primary → deselect all
        setSelectedNodeId(null);
        setSecondNodeId(null);
      } else if (secondNodeId === nodeId) {
        // Re-clicking second → deselect only second
        setSecondNodeId(null);
      } else if (secondNodeId === null) {
        // One selected, click a different node → set as second (no auto-connect)
        setSecondNodeId(nodeId);
      } else {
        // Two already selected, click a third → reset to new primary
        setSelectedNodeId(nodeId);
        setSecondNodeId(null);
      }
    },
    [dragState, selectedNodeId, secondNodeId]
  );

  const handleNodeMouseDown = useCallback(
    (e, nodeId) => {
      e.stopPropagation();
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;
      setDragState({
        nodeId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        origXRatio: node.x_ratio,
        origYRatio: node.y_ratio,
      });
    },
    [nodes]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!dragState || !containerRef.current) return;
      const cRect = containerRef.current.getBoundingClientRect();
      const iRect = imgRef.current.getBoundingClientRect();

      const relX = e.clientX - cRect.left - (iRect.left - cRect.left);
      const relY = e.clientY - cRect.top - (iRect.top - cRect.top);

      let x_ratio = Math.max(0, Math.min(1, relX / iRect.width));
      let y_ratio = Math.max(0, Math.min(1, relY / iRect.height));

      // Snap to nearby nodes' X or Y axis within threshold
      let snapX = null;
      let snapY = null;
      for (const other of nodes) {
        if (other.id === dragState.nodeId) continue;
        if (snapX === null && Math.abs(x_ratio - other.x_ratio) < SNAP_THRESHOLD) {
          snapX = other.x_ratio;
          x_ratio = other.x_ratio;
        }
        if (snapY === null && Math.abs(y_ratio - other.y_ratio) < SNAP_THRESHOLD) {
          snapY = other.y_ratio;
          y_ratio = other.y_ratio;
        }
        if (snapX !== null && snapY !== null) break;
      }

      setSnapIndicator(snapX !== null || snapY !== null ? { snapX, snapY } : null);
      setNodes((prev) =>
        prev.map((n) => (n.id === dragState.nodeId ? { ...n, x_ratio, y_ratio } : n))
      );
    },
    [dragState, nodes]
  );

  const handleMouseUp = useCallback(() => {
    setDragState(null);
    setSnapIndicator(null);
  }, []);

  const updateSelectedNode = useCallback(
    (field, value) => {
      setNodes((prev) =>
        prev.map((n) => (n.id === selectedNodeId ? { ...n, [field]: value } : n))
      );
    },
    [selectedNodeId]
  );

  const hasEdge = useCallback(
    (aId, bId) =>
      edges.some(
        (e) =>
          (e.from_id === aId && e.to_id === bId) ||
          (e.from_id === bId && e.to_id === aId)
      ),
    [edges]
  );

  const handleSave = useCallback(async () => {
    if (!mapId) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "store_maps", mapId), {
        nodes,
        edges,
        updated_at: serverTimestamp(),
      });
      setSnackbar({ open: true, msg: "Graph saved successfully!", severity: "success" });
    } catch (e) {
      setSnackbar({ open: true, msg: `Save failed: ${e.message}`, severity: "error" });
    } finally {
      setSaving(false);
    }
  }, [nodes, edges, mapId]);

  const handleAutoLink = useCallback(async () => {
    if (!mapId || nodes.length === 0) {
      setSnackbar({ open: true, msg: "Add nodes first before auto-linking.", severity: "warning" });
      return;
    }
    setLinking(true);
    try {
      const snap = await getDocs(PRODUCTS_COLLECTION);
      let count = 0;
      const updates = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        const coords = data.map_coords?.[mapId];
        if (!coords || coords.x_ratio == null || coords.y_ratio == null) return;
        let nearestId = null;
        let minD = Infinity;
        for (const node of nodes) {
          const d = dist({ x_ratio: coords.x_ratio, y_ratio: coords.y_ratio }, node);
          if (d < minD) { minD = d; nearestId = node.id; }
        }
        if (nearestId) {
          updates.push(
            updateDoc(doc(db, "products", docSnap.id), {
              [`map_coords.${mapId}.nearest_node_id`]: nearestId,
            })
          );
          count++;
        }
      });
      await Promise.all(updates);
      setSnackbar({ open: true, msg: `Linked ${count} products to nearest nodes.`, severity: "success" });
    } catch (e) {
      setSnackbar({ open: true, msg: `Auto-link failed: ${e.message}`, severity: "error" });
    } finally {
      setLinking(false);
    }
  }, [nodes, mapId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Compute snap guide line pixel positions for SVG rendering
  const snapLines = (() => {
    if (!snapIndicator || !imgRef.current || !containerRef.current) return [];
    const iRect = imgRef.current.getBoundingClientRect();
    const cRect = containerRef.current.getBoundingClientRect();
    const imgLeft = iRect.left - cRect.left;
    const imgTop = iRect.top - cRect.top;
    const imgRight = imgLeft + iRect.width;
    const imgBottom = imgTop + iRect.height;
    const lines = [];
    if (snapIndicator.snapX !== null) {
      const x = imgLeft + snapIndicator.snapX * iRect.width;
      lines.push({ key: "sx", x1: x, y1: imgTop, x2: x, y2: imgBottom });
    }
    if (snapIndicator.snapY !== null) {
      const y = imgTop + snapIndicator.snapY * iRect.height;
      lines.push({ key: "sy", x1: imgLeft, y1: y, x2: imgRight, y2: y });
    }
    return lines;
  })();

  const pairConnected = selectedNodeId && secondNodeId && hasEdge(selectedNodeId, secondNodeId);

  const statusMsg = secondNodeId
    ? `2 nodes selected · Press C to ${pairConnected ? "disconnect" : "connect"} · Delete removes primary node`
    : selectedNodeId
    ? `Selected: ${selectedNode?.label || selectedNode?.type} · Click another node to pair · Delete to remove`
    : "Click on the map to add a route node";

  return (
    <Box
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      sx={{ userSelect: "none" }}
    >
      {/* Toolbar */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ background: "#10b981" }}
        >
          Save Graph
        </Button>
        <Button
          variant="outlined"
          startIcon={linking ? <CircularProgress size={16} /> : <AutoLinkIcon />}
          onClick={handleAutoLink}
          disabled={linking}
        >
          Auto-link Products
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {nodes.length} nodes · {edges.length} edges
        </Typography>
      </Stack>

      {/* Hint */}
      <Alert severity="info" sx={{ mb: 2, fontSize: "0.8rem" }}>
        <strong>Click empty map</strong> to add node ·{" "}
        <strong>Click node</strong> to select ·{" "}
        <strong>Click second node</strong> to pair ·{" "}
        <strong>C</strong> connect/disconnect ·{" "}
        <strong>Delete</strong> remove ·{" "}
        <strong>↑ ↓ ← →</strong> nudge (+ Shift = 5×) ·{" "}
        <strong>Drag</strong> to move (snaps to nearby axes)
      </Alert>

      <Box sx={{ display: "flex", gap: 2 }}>
        {/* Map canvas */}
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            position: "relative",
            minHeight: 500,
            background: "#111",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {mapUrl ? (
            <img
              ref={imgRef}
              src={mapUrl}
              alt="Store map"
              onLoad={updateImgRect}
              style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
            />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 400, color: "#aaa" }}>
              No map image uploaded. Upload one in the Product Pins tab first.
            </Box>
          )}

          {/* SVG overlay */}
          <svg
            ref={svgRef}
            onClick={handleSvgClick}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              cursor: "crosshair",
              overflow: "visible",
            }}
          >
            <defs>
              {/* Soft glow for selected-node rings */}
              <filter id="re-node-glow" x="-60%" y="-60%" width="220%" height="220%">
                <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="rgba(239,68,68,0.7)" />
              </filter>
              <filter id="re-node-glow2" x="-60%" y="-60%" width="220%" height="220%">
                <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="rgba(250,204,21,0.7)" />
              </filter>
            </defs>
            {/* Axis snap guide lines (shown while dragging) */}
            {snapLines.map((line) => (
              <line
                key={line.key}
                x1={line.x1} y1={line.y1}
                x2={line.x2} y2={line.y2}
                stroke="#facc15"
                strokeWidth={1}
                strokeDasharray="5 3"
                opacity={0.8}
                style={{ pointerEvents: "none" }}
              />
            ))}

            {/* Edges — always visible, green by default, yellow when pair selected.
                Two-pass draw (dark underlay + colored line) avoids SVG filter
                bounding-box collapse on perfectly horizontal/vertical lines. */}
            {edges.map((edge, i) => {
              const fromNode = nodes.find((n) => n.id === edge.from_id);
              const toNode = nodes.find((n) => n.id === edge.to_id);
              if (!fromNode || !toNode) return null;
              const p1 = ratioToSvg(fromNode.x_ratio, fromNode.y_ratio);
              const p2 = ratioToSvg(toNode.x_ratio, toNode.y_ratio);
              const isPair =
                (edge.from_id === selectedNodeId && edge.to_id === secondNodeId) ||
                (edge.from_id === secondNodeId && edge.to_id === selectedNodeId);
              const edgeColor = isPair ? "#facc15" : "#22c55e";
              const edgeWidth = isPair ? 3.5 : 2.5;
              return (
                <g key={i} style={{ pointerEvents: "none" }}>
                  {/* Dark underlay for contrast over any map background */}
                  <line
                    x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                    stroke="rgba(0,0,0,0.65)"
                    strokeWidth={edgeWidth + 2.5}
                    strokeLinecap="round"
                  />
                  {/* Colored route line */}
                  <line
                    x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                    stroke={edgeColor}
                    strokeWidth={edgeWidth}
                    strokeLinecap="round"
                  />
                </g>
              );
            })}

            {/* Nodes */}
            {nodes.map((node) => {
              const { x, y } = ratioToSvg(node.x_ratio, node.y_ratio);
              const isFirst = node.id === selectedNodeId;
              const isSecond = node.id === secondNodeId;
              const color = NODE_COLORS[node.type] || "#757575";
              return (
                <g
                  key={node.id}
                  transform={`translate(${x}, ${y})`}
                  onClick={(e) => handleNodeClick(e, node.id)}
                  onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                  style={{ cursor: dragState?.nodeId === node.id ? "grabbing" : "grab" }}
                >
                  {/* Primary selection: red glow halo + solid red ring */}
                  {isFirst && (
                    <>
                      <circle r={20} fill="rgba(239,68,68,0.18)" stroke="none" style={{ pointerEvents: "none" }} />
                      <circle r={14} fill="none" stroke="#ef4444" strokeWidth={3} filter="url(#re-node-glow)" style={{ pointerEvents: "none" }} />
                    </>
                  )}
                  {/* Second selection: yellow glow halo + solid yellow ring */}
                  {isSecond && (
                    <>
                      <circle r={20} fill="rgba(250,204,21,0.15)" stroke="none" style={{ pointerEvents: "none" }} />
                      <circle r={14} fill="none" stroke="#facc15" strokeWidth={3} filter="url(#re-node-glow2)" style={{ pointerEvents: "none" }} />
                    </>
                  )}
                  {/* Node body */}
                  <circle
                    r={isFirst || isSecond ? 10 : 8}
                    fill={color}
                    stroke={isFirst ? "#ef4444" : isSecond ? "#facc15" : "#fff"}
                    strokeWidth={isFirst || isSecond ? 2.5 : 2}
                  />
                  {node.label ? (
                    <text
                      y={-16}
                      textAnchor="middle"
                      fill="#fff"
                      fontSize={10}
                      style={{ pointerEvents: "none", filter: "drop-shadow(0 1px 2px #000)" }}
                    >
                      {node.label}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </svg>

          {/* Status bar */}
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              left: 8,
              right: 8,
              background: "rgba(0,0,0,0.65)",
              color: "#fff",
              borderRadius: 1.5,
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
            }}
          >
            {statusMsg}
          </Box>
        </Box>

        {/* Right panel — node properties or legend */}
        <Box sx={{ width: 240, flexShrink: 0 }}>
          {selectedNode ? (
            <Box sx={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 2, p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ color: "#10b981", fontWeight: 700 }}>
                  Node Properties
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => { setSelectedNodeId(null); setSecondNodeId(null); }}
                  sx={{ color: "#aaa" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>

              <TextField
                label="Label"
                size="small"
                fullWidth
                value={selectedNode.label}
                onChange={(e) => updateSelectedNode("label", e.target.value)}
                placeholder="e.g. Entrance A, Aisle 3"
                sx={{ mb: 1.5 }}
                InputLabelProps={{ style: { color: "#aaa" } }}
                inputProps={{ style: { color: "#fff" } }}
              />

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel sx={{ color: "#aaa" }}>Type</InputLabel>
                <Select
                  value={selectedNode.type}
                  label="Type"
                  onChange={(e) => updateSelectedNode("type", e.target.value)}
                  sx={{ color: "#fff" }}
                >
                  {NODE_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: NODE_COLORS[t] }} />
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ borderColor: "#2a2a4a", mb: 2 }} />

              <Typography variant="caption" sx={{ color: "#aaa", display: "block", mb: 1 }}>
                Position: ({selectedNode.x_ratio.toFixed(3)}, {selectedNode.y_ratio.toFixed(3)})
              </Typography>

              <Typography variant="caption" sx={{ color: "#aaa", display: "block", mb: 2 }}>
                Connections: {edges.filter((e) => e.from_id === selectedNodeId || e.to_id === selectedNodeId).length}
              </Typography>

              {/* Connect/Disconnect button shown when a pair is selected */}
              {secondNodeId && (
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={pairConnected ? <LinkOffIcon /> : <LinkIcon />}
                  onClick={() => toggleEdge(selectedNodeId, secondNodeId)}
                  sx={{ mb: 1, borderColor: "#facc15", color: "#facc15", "&:hover": { borderColor: "#eab308" } }}
                >
                  {pairConnected ? "Disconnect Pair" : "Connect Pair"}
                </Button>
              )}

              <Button
                variant="outlined"
                color="error"
                size="small"
                fullWidth
                startIcon={<DeleteIcon />}
                onClick={handleDeleteNode}
              >
                Delete Node
              </Button>
            </Box>
          ) : (
            <Box sx={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "#aaa", mb: 1 }}>
                Node Legend
              </Typography>
              {NODE_TYPES.map((t) => (
                <Box key={t} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", background: NODE_COLORS[t], flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ color: "#ccc", textTransform: "capitalize" }}>{t}</Typography>
                </Box>
              ))}
              <Divider sx={{ borderColor: "#2a2a4a", my: 1.5 }} />
              <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 0.5, fontWeight: 600 }}>
                Keyboard shortcuts
              </Typography>
              <Typography variant="caption" sx={{ color: "#888", display: "block" }}>
                Delete — remove selected node
              </Typography>
              <Typography variant="caption" sx={{ color: "#888", display: "block" }}>
                C — connect / disconnect pair
              </Typography>
              <Typography variant="caption" sx={{ color: "#888", display: "block" }}>
                ↑ ↓ ← → — nudge node
              </Typography>
              <Typography variant="caption" sx={{ color: "#888", display: "block" }}>
                Shift + ↑ ↓ ← → — nudge ×5
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
